import { useCallback, useState } from "react";
import { IconButton, List, ListItem, ListItemText, TextField } from "@mui/material";
import { useStyles } from "./CrewInput.styles";
import { ICrewInput } from "./CrewInputInterfaces";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { ICrew } from "../MillSettingsStepInterfaces";

const CrewInput = ({ values, setFieldValue, handleChange, editCrewField, setEditCrewField }: ICrewInput) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [sameCrewNameError, setSameCrewNameError] = useState<boolean>(false);
    const [sameCrewNameEditionError, setSameCrewNameEditionError] = useState<boolean>(false);

    const handleCrewInputChange = useCallback(() => {
        if (Boolean(values.crewInput?.trim())) {
            const duplicatedValues = values.crewList.findIndex((crew) => {
                return !Boolean(crew.deleted_at) && crew.name.toLowerCase() === values.crewInput.trim().toLowerCase();
            });

            if (duplicatedValues !== -1) {
                setSameCrewNameError(true);
                return;
            } else {
                setSameCrewNameError(false);
            }

            setFieldValue("crewList", [
                ...values.crewList,
                {
                    name: values.crewInput,
                },
            ]);
            setFieldValue("crewInput", "");
        }
    }, [values.crewInput, values.crewList, setFieldValue]);

    const handleCrewEditionChange = useCallback(
        (index: number) => {
            if (Boolean(values.crewEditInput?.trim())) {
                const duplicatedValues = values.crewList.findIndex((crew) => {
                    return (
                        !Boolean(crew.deleted_at) &&
                        crew.name.toLowerCase() === values.crewEditInput.trim().toLowerCase()
                    );
                });

                if (duplicatedValues !== -1 && duplicatedValues !== index) {
                    setSameCrewNameEditionError(true);
                    return;
                } else {
                    setSameCrewNameEditionError(false);
                }

                const crewList = [...values.crewList];
                crewList[index] = {
                    ...crewList[index],
                    name: values.crewEditInput,
                };

                setFieldValue("crewList", crewList);
                setEditCrewField(null);
            }
        },
        [values.crewEditInput, values.crewList, setFieldValue, setEditCrewField],
    );

    const handleCrewCancelEdition = useCallback(() => setEditCrewField(null), [setEditCrewField]);

    const handleCrewDeletion = useCallback(
        (crew: ICrew, index: number) => {
            const crewList = [...values.crewList];
            crewList[index] = {
                ...crewList[index],
                deleted_at: new Date(),
            };

            if (crew.crew_id) {
                setFieldValue("crewList", crewList);
            } else {
                setFieldValue(
                    "crewList",
                    values.crewList.filter((_, indexFilter) => indexFilter !== index),
                );
            }
        },
        [values.crewList, setFieldValue],
    );

    return (
        <>
            <div className={classes.formSubtitle}>{t("mill_setup.crew.title")}</div>
            <TextField
                fullWidth
                id="crew-input"
                name="crewInput"
                type="text"
                variant="outlined"
                className={classes.textField}
                label={t("mill_setup.crew.title")}
                error={sameCrewNameError}
                helperText={sameCrewNameError && t("mill_setup.error_message.existing_crew_name")}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        handleCrewInputChange();
                    }
                }}
                value={values.crewInput}
                InputProps={{
                    endAdornment: (
                        <IconButton onClick={handleCrewInputChange}>
                            <AddIcon />
                        </IconButton>
                    ),
                }}
                onChange={handleChange}
            />

            {Boolean(values.crewList.length) && (
                <List className={classes.listContainer}>
                    {values.crewList.map((crew, index) => {
                        return (
                            <ListItem
                                style={{ ...(crew.deleted_at && { display: "none" }) }}
                                key={index}
                                className={classes.listItem}
                            >
                                {editCrewField !== index && (
                                    <ListItemText className={classes.listText} primary={crew.name} />
                                )}

                                {editCrewField === index && (
                                    <TextField
                                        fullWidth
                                        label={t("mill_setup.crew.edit_name")}
                                        variant="outlined"
                                        autoFocus={true}
                                        name="crewEditInput"
                                        onChange={handleChange}
                                        value={values.crewEditInput}
                                        error={sameCrewNameEditionError}
                                        helperText={
                                            sameCrewNameEditionError && t("mill_setup.error_message.existing_crew_name")
                                        }
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                handleCrewEditionChange(index);
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <>
                                                    <IconButton onClick={() => handleCrewEditionChange(index)}>
                                                        <CheckIcon />
                                                    </IconButton>
                                                    <IconButton onClick={handleCrewCancelEdition}>
                                                        <CloseIcon />
                                                    </IconButton>
                                                </>
                                            ),
                                        }}
                                    />
                                )}

                                {editCrewField !== index && (
                                    <>
                                        <IconButton
                                            onClick={() => {
                                                setFieldValue("crewEditInput", crew.name);
                                                setEditCrewField(index);
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleCrewDeletion(crew, index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </>
                                )}
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </>
    );
};

export default CrewInput;
